import { EditablePersonInfo } from '@/data/Content/PersonInfo';
import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { ACCOUNT_TABS } from '@/components/content/Account/parts/Tabs';
import { AccountSummaryPersonalInfo } from '@/components/content/Account/parts/SummaryPersonalInfo';
import { AccountSummaryTitleSX } from '@/components/content/Account/styles/summary';
import { useClubDpCards } from '@/data/Content/ClubDp';
import { useOrderHistoryWithDetails } from '@/data/Content/OrderHistory';
import { AccountSummaryOrders } from '@/components/content/Account/parts/AccountSummaryOrders';
import { ContentProvider } from '@/data/context/content';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import React, { useMemo } from 'react';
import { AccountSummaryDpvale } from '@/components/content/Account/parts/AccountSummaryDpvale';
import { SummarySkeleton } from '@/components/content/Account/parts/Skeletons/SummarySkeleton';
import { AccountSummaryMonedero } from '@/components/content/Account/parts/AccountSummaryMonedero';
import { useValesDeCaja } from '@/data/Content/ValesDeCaja';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { NullElement } from '@/components/blocks/NullElement';

interface AccountSummaryProps {
	personInfo: EditablePersonInfo;
	onSelectedTab: (tab: ACCOUNT_TABS) => void;
}

export const AccountSummary: React.FC<AccountSummaryProps> = ({ personInfo, onSelectedTab }) => {
	const { rawData, loading } = useClubDpCards();
	const { data: valesDeCaja } = useValesDeCaja({ enabled: true });

	const { hasMonedero, balance } = useMemo(
		() => ({
			hasMonedero: valesDeCaja?.balance !== undefined && valesDeCaja?.balance !== null,
			balance: Number(valesDeCaja?.balance ?? 0),
		}),
		[valesDeCaja]
	);

	const orderHistoryValues = useOrderHistoryWithDetails({
		pageNumber: 1,
		pageSize: 3,
	});
	const isMobile = useIsMobile();

	const orders = useMemo(() => {
		const [recentOrder] = orderHistoryValues.ordersWithItemsDetails;
		return recentOrder ? [recentOrder] : [];
	}, [orderHistoryValues.ordersWithItemsDetails]);

	if (loading || orderHistoryValues.loading) {
		return <SummarySkeleton />;
	}

	const linkedDpVale = !!rawData?.linked.puntosDpVale;

	const elementOrders = (
		<React.Fragment>
			{orders.length > 0 ? (
				<ContentProvider value={{ ...orderHistoryValues }}>
					<Divider sx={{ marginTop: 3, marginBottom: 3 }} />
					<AccountSummaryOrders
						orders={orderHistoryValues.ordersWithItemsDetails}
						mobile={isMobile}
						onReOrder={orderHistoryValues.onReOrder}
						onSelectedTab={onSelectedTab}
					/>
				</ContentProvider>
			) : null}
		</React.Fragment>
	);

	const elementDpvale = (
		<React.Fragment>
			<Divider sx={{ marginTop: 3, marginBottom: 3 }} />
			<AccountSummaryDpvale rawData={rawData} onSelectedTab={onSelectedTab} />
		</React.Fragment>
	);

	const elementMonedero = hasMonedero ? (
		<React.Fragment>
			<AccountSummaryMonedero balance={balance} onSelectedTab={onSelectedTab} />
			<Divider sx={{ marginTop: 2, marginBottom: 2, borderColor: 'transparent' }} />
		</React.Fragment>
	) : null;

	return (
		<Box>
			<Typography component={'h1'} sx={AccountSummaryTitleSX}>
				{'RESUMEN DE TU CUENTA'}
			</Typography>
			{elementMonedero}
			<AccountSummaryPersonalInfo personInfo={personInfo} onSelectedTab={onSelectedTab} />
			<ContentRecommendation
				id="DPSV2AccountDpFanCard"
				properties={{
					emsName: 'DPSV2AccountDpFanCard',
				}}
				withoutFrame
				loadingElement={
					<Skeleton
						variant="rectangular"
						sx={{ mt: '24px', width: '100%', height: { xs: '192px', md: '48px' } }}
					/>
				}
				emptyElement={<NullElement />}
			/>
			{linkedDpVale ? (
				<React.Fragment>
					{elementDpvale}
					{elementOrders}
				</React.Fragment>
			) : (
				<React.Fragment>
					{elementOrders}
					{elementDpvale}
				</React.Fragment>
			)}
		</Box>
	);
};
